import { downloadFileAttachment } from '#pplx/frontend/apps/ask/queries/mutations/filesMutations.js';
import { isImage } from '#pplx/frontend/common/files/images.js';
import { isCloudinaryURL } from '#pplx/frontend/common/files/urls.js';
import { useTrackedFileUploadAnalytics } from '#pplx/frontend/components/hooks/fileUploadAnalytics.js';
import { useUploadCloudFiles } from '#pplx/frontend/composer-sdk-plugins/file-attachments/cloud/useUploadCloudFiles.js';
import { useCallback } from 'react';
/**
 * Applies Ask upload policy and adds remote preview targets to accepted cloud attachments.
 *
 * @remarks
 * Upload and processing snapshots reach the consumer before preview authorization finishes.
 * Preview failure leaves the accepted attachment submittable with its canonical URL. Each
 * preview activation requests fresh authorization; signed URLs are not used for submission.
 * Abort and removal checks suppress preview work for attachments no longer owned by ingress.
 *
 * @param options - Ask policy and the ingress-owned set of removed attachment IDs.
 * @returns Transport operations with preview projection added to upload progress.
 */
export function useCloudFileAttachmentUpload({ reason, removedIdsRef, uploadPolicy, }) {
    const cloudAnalytics = useTrackedFileUploadAnalytics('thread_attachment');
    const cloudTransport = useUploadCloudFiles({
        analytics: cloudAnalytics,
        defaultFileSource: uploadPolicy.transport.defaultFileSource,
        maxFileSizeMB: uploadPolicy.constraints.maxFileSizeMB,
        onRateLimitUpdate: uploadPolicy.quota.consumeUploadCount,
        reason,
        skipMimeValidation: uploadPolicy.transport.skipParsing,
        uploadRateLimit: uploadPolicy.quota.remainingUploadCount,
    });
    const { uploadFiles: uploadCloudDocuments } = cloudTransport;
    const uploadCloudFilesWithPreview = useCallback(async (documents, onChange, signal) => {
        const uploaded = await uploadCloudDocuments(documents, onChange, signal);
        await Promise.all(uploaded.map(async (file) => {
            if (signal?.aborted ||
                removedIdsRef.current.has(file.id) ||
                file.status !== 'success' ||
                !file.url)
                return;
            const canonicalUrl = file.url;
            try {
                const resolveUrl = async () => {
                    if (signal?.aborted || removedIdsRef.current.has(file.id))
                        return undefined;
                    const { file_url } = await downloadFileAttachment({
                        request: { url: canonicalUrl, thread_id: '' },
                        reason,
                    });
                    return signal?.aborted || removedIdsRef.current.has(file.id) ? undefined : file_url;
                };
                const canReusePreviewUrl = file.previewUrl !== undefined &&
                    (isCloudinaryURL(file.url) || isImage(file.fileName) || isImage(file.file));
                // Fresh S3 attachments are authorized by uploader; no thread entry exists yet.
                const url = canReusePreviewUrl ? file.previewUrl : await resolveUrl();
                if (signal?.aborted || !url)
                    return;
                const previewFile = {
                    ...file,
                    previewTarget: { kind: 'remote', fileName: file.fileName, url: resolveUrl },
                };
                onChange?.([previewFile]);
            }
            catch {
                // Preview authorization must not discard an accepted upload or block submission.
            }
        }));
    }, [uploadCloudDocuments, reason, removedIdsRef]);
    return { ...cloudTransport, uploadFiles: uploadCloudFilesWithPreview };
}
//# sourceMappingURL=useCloudFileAttachmentUpload.js.map